import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { Box, IconButton, Slide, Snackbar, Typography } from '@mui/material'
import type { TransitionProps } from '@mui/material/transitions'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { ToastContext, type ToastType } from '@/shared/contexts/ToastContext'
import { RADIUS } from '@/shared/styles/radii'

// latest app の Toast. 28 spec §4.6 準拠の dark surface.
//   surface : dark.bgSubtle (#1F1F1F) + popover shadow / radius 8 (border なし)
//   text    : #FFFFFF (§4.6) / title 14/500 / close は dark.fgMuted
//   state   : success/warning/error は先頭に state 色のアイコンを 1 つ置く. info はなし
//             (= 通常通知は地と同じ uniform な dark で出す). 色ドットでなくアイコンで示す
//             (= Notion の status toast 流. 同一地色に点だけより識別性が高い).
//   width 340 固定 (= calc(100vw - 48px) cap) / position bottom-left 24px (注意散漫しない場所)
//   enter Slide up 250ms ease-out / exit Slide down 180ms ease-in
// shared/styles の theme には依存しない (= latest app 削除時に本 file ごと消える).
const STATE_ICON: Partial<
  Record<ToastType, { Icon: typeof CheckCircleOutlineIcon; color: string }>
> = {
  success: { Icon: CheckCircleOutlineIcon, color: colorTokens.state.successFg },
  warning: { Icon: WarningAmberRoundedIcon, color: colorTokens.state.warnFg },
  error: { Icon: ErrorOutlineIcon, color: colorTokens.state.dangerFg },
}

const DEFAULT_DURATION: Record<ToastType, number> = {
  info: 3000,
  success: 3000,
  warning: 4000,
  error: 4000,
}

const SlideUp = React.forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement<unknown, string> },
  ref: React.Ref<unknown>
) {
  return <Slide {...props} direction="up" ref={ref} />
})

export const LatestToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [type, setType] = useState<ToastType>('info')
  const [duration, setDuration] = useState<number>(DEFAULT_DURATION.info)
  // open=true 中の連続 showToast race ガード. 一旦 close → 100ms 待って次 message を出す.
  // isProcessing がその 100ms 内の追加呼出しを drop する.
  const [isProcessing, setIsProcessing] = useState(false)

  const showToast = useCallback(
    (
      newMessage: string,
      newType: ToastType = 'info',
      autoHideDuration?: number
    ) => {
      if (isProcessing) return

      const applyNext = () => {
        setMessage(newMessage)
        setType(newType)
        setDuration(autoHideDuration ?? DEFAULT_DURATION[newType])
        setOpen(true)
      }

      if (open) {
        setIsProcessing(true)
        setOpen(false)
        window.setTimeout(() => {
          applyNext()
          setIsProcessing(false)
        }, 100)
      } else {
        applyNext()
      }
    },
    [open, isProcessing]
  )

  const hideToast = useCallback(() => {
    setOpen(false)
  }, [])

  useEffect(() => {
    if (!open || duration <= 0) return
    const id = window.setTimeout(() => hideToast(), duration)
    return () => window.clearTimeout(id)
  }, [open, duration, hideToast])

  const stateIcon = STATE_ICON[type]

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Snackbar
        open={open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoHideDuration={duration}
        onClose={hideToast}
        TransitionComponent={SlideUp}
        transitionDuration={{ enter: 250, exit: 180 }}
        sx={{ left: 24, bottom: 24 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: colorTokens.dark.bgSubtle,
            color: '#FFFFFF',
            borderRadius: `${RADIUS.lg}px`,
            boxShadow: colorTokens.shadow.popover,
            px: 2,
            py: 1.5,
            width: 340,
            maxWidth: 'calc(100vw - 48px)',
          }}
        >
          {stateIcon && (
            <stateIcon.Icon
              sx={{ fontSize: 18, color: stateIcon.color, flexShrink: 0 }}
            />
          )}
          <Typography
            sx={{
              flex: 1,
              fontSize: 14,
              lineHeight: 1.5,
              fontWeight: 500,
              color: '#FFFFFF',
            }}
          >
            {message}
          </Typography>
          <IconButton
            size="small"
            onClick={hideToast}
            aria-label="close"
            sx={{
              color: colorTokens.dark.fgMuted,
              p: 0.25,
              '&:hover': {
                color: '#FFFFFF',
                bgcolor: 'transparent',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Snackbar>
    </ToastContext.Provider>
  )
}
